import React from 'react';
import styled from 'styled-components';

import { hasOrbitAnswer } from 'api/orbit';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { useOrbitContext } from '../OrbitContext';
import { orbitAnswerConfidence } from '../answerConfidence';
import { orbitMetricName } from '../chartAdapters';
import { OrbitGlyphSplit, OrbitGlyphIcon, splitOrbitGlyph } from '../glyphIcons';
import { TOrbit } from '../i18n/TModules';
import { getOrbitOutcomeCopy } from '../outcomeCopy';
import { orbitPlannedWindowDays } from '../plannedWindow';
import {
  OrbitAnswerLabel,
  OrbitBodyText,
  OrbitChip,
  OrbitChipRow,
  OrbitSectionLabel,
  orbitSpace,
  orbitTokens,
  orbitType
} from '../styles';
import { OrbitOutcome, OrbitTurn } from '../types';
import OrbitAccessCard from './OrbitAccessCard';
import OrbitActionRow from './OrbitActionRow';
import OrbitAnalysisCard, { OrbitAnalysisVariant } from './OrbitAnalysisCard';
import OrbitAnswerFooter from './OrbitAnswerFooter';
import OrbitReportStatus from './OrbitReportStatus';

export interface IOrbitAnswerProps {
  turn: OrbitTurn;
  chart?: React.ReactNode;
}

// figma-spec-v2.md §4a puts 13 Regular #F5F7FB on single-line idle-panel rows and 12.5 Regular
// #AEBBD0 on analysis body copy. These values are answer body copy, so they take the second role and
// let the uppercase label above carry the rank.
const AnswerText = styled(OrbitBodyText)`
  color: ${orbitTokens.textSecondary};
  ${orbitType.conversation}
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

// The detail rows take the card's own 8px rhythm, not a second one: at 12 the gap between two rows
// was wider than the gap between whole blocks, which read as the rows belonging to no group.
const Rows = styled.dl`
  margin: 0;

  /* PERX3-REDESIGN round 6: SCOPE, the chart and EXPLANATION are sections of the card and take the
     card's own section gap under the flag; the drawn 8 is what the fallback keeps. The doubled
     class outranks the later single-class rules that had collapsed the gap to 0. */
  && > * + * {
    margin-top: ${orbitSpace('space-section', '8px')};
  }
`;

// A section label binds to the body under it at the same gap in both readings; it is declared so
// the whole rhythm reads off `ORBIT_SPACE_LIGHT` rather than one gap living only here.
const Row = styled.div`
  && > * + * {
    margin-top: ${orbitSpace('space-label', '6px')};
  }
`;

const RowContent = styled.dd`
  margin: 0;
`;

// div.sq-title 4952:8121 carries pad-top 8 on top of the card's own 8px gap, so the heading binds
// downward to its action row rather than sitting midway between two blocks.
const SuggestedLabel = styled(OrbitAnswerLabel)`
  padding-top: 8px;
`;

const ChartSlot = styled.div`
  margin: 0;
`;

/**
 * Which outcomes the analysis card contains. v3 draws `div.analysis-card` around a governed result
 * (`5017:9864`) and around a generated report (`5032:11580`); a plain prose reply is drawn as the
 * assistant `div.msgBubble` (`5032:12076`) with no card at all. Routing every terminal outcome
 * through the card gave a clarify reply the bars variant, whose rule is transparent by design, so
 * the reply rendered with no visible edge.
 */
const CARD_OUTCOMES: ReadonlySet<OrbitOutcome> = new Set<OrbitOutcome>([
  'answered',
  'report_queued'
]);

export const drawsAnalysisCard = (turn: OrbitTurn): boolean =>
  hasOrbitAnswer(turn) && turn.outcome !== null && CARD_OUTCOMES.has(turn.outcome);

const Prose = styled.div`
  > * + * {
    margin-top: 6px;
  }
`;

const ProseLabel = styled.h4<{ $negative: boolean }>`
  display: flex;
  align-items: center;
  margin: 0;
  color: ${({ $negative }) => ($negative ? orbitTokens.negative : orbitTokens.action)};
  ${orbitType.conversation}
  font-weight: 700;

  > * + * {
    margin-left: 6px;
  }
`;

// The bubble's own ink, #EAF0F7 (`5032:12077`), not the card body's #AEBBD0: this copy is the whole
// reply rather than a caption under a heading.
const ProseText = styled.p`
  margin: 0;
  color: ${orbitTokens.threadText};
  ${orbitType.conversation}
  font-weight: 400;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

interface IDetailRowProps {
  id: string;
  label: string;
  value: string;
}

const DetailRow: React.FC<IDetailRowProps> = ({ id, label, value }) =>
  value === '' ? null : (
    <Row data-testid={`orbit-answer-${id}`}>
      <OrbitSectionLabel as="dt">{label}</OrbitSectionLabel>
      <AnswerText as="dd">{value}</AnswerText>
    </Row>
  );

const OrbitAnswer: React.FC<IOrbitAnswerProps> = ({ turn, chart }) => {
  const { t } = useMultiLanguageTranslation();
  const perx3 = usePerx3Redesign();
  const { ask, isAsking } = useOrbitContext();
  /*
    An evidence chip is the business NAME of the metric the answer stands on, never its catalogue
    id: `O-COM-011` in the middle of a user-facing explanation is the pipeline's private language
    (PLA-9563 section 3a). The turn's own `metric_results[].metric_name` is preferred over the
    registry's label because it is what the server computed this answer from; the id survives on the
    chip's `title` so Support can still trace a reading back to the catalogue.
  */
  const evidenceName = (metricId: string): string =>
    orbitMetricName(
      metricId,
      turn.metric_results.find(result => result.metric_id === metricId)?.metric_name
    );

  if (!hasOrbitAnswer(turn)) {
    return null;
  }

  const { answer } = turn;
  const outcomeCopy = getOrbitOutcomeCopy(turn);
  // Both sources of a reading, in the shared badge's own shape. `null` where the server sent
  // neither a governed metric confidence nor any answer-level text.
  const confidence = orbitAnswerConfidence(answer.confidence, turn.metric_results);
  // `4952:8536` heads the donut and chips answer with the smaller governed-analysis tag. The bar
  // answer's "Orbit AI Insight" head was removed: no current frame draws it.
  const variant: OrbitAnalysisVariant = answer.visualization === 'donut' ? 'donut' : 'bars';
  const headKey = `${TOrbit}.analysis.${variant === 'donut' ? 'governed' : 'insight'}`;
  const analysisLabel: OrbitGlyphSplit = perx3 ? splitOrbitGlyph(t(headKey)) : { rest: t(headKey) };

  /*
    `not_configured` (PLA-9567 / PLA-9579) is the one outcome that can arrive with no prose behind
    it: there is no measurement to describe, only a setup that does not exist. Every other outcome
    carries the backend's own sentence and is rendered as sent.
  */
  const proseText =
    answer.answer === '' && turn.outcome === 'not_configured'
      ? t(`${TOrbit}.outcome.notConfiguredBody`)
      : answer.answer;

  const days = turn.planned_window ? orbitPlannedWindowDays(turn.planned_window) : null;
  let period = '';
  if (days !== null && days.first === null) {
    period = t(`${TOrbit}.answer.periodUntil`, { last: days.last });
  } else if (days !== null) {
    period =
      days.first === days.last
        ? days.last
        : t(`${TOrbit}.answer.periodRange`, { first: days.first, last: days.last });
  }
  const periodRow = <DetailRow id="period" label={t(`${TOrbit}.answer.period`)} value={period} />;

  if (!drawsAnalysisCard(turn)) {
    return (
      <Prose data-testid="orbit-answer">
        {outcomeCopy !== null ? (
          <ProseLabel
            $negative={outcomeCopy.tone === 'negative'}
            data-testid="orbit-answer-outcome"
          >
            <outcomeCopy.Icon aria-hidden="true" />
            <span>{t(outcomeCopy.labelKey)}</span>
          </ProseLabel>
        ) : null}
        <ProseText data-testid="orbit-answer-text">{proseText}</ProseText>
        {period !== '' ? <Rows>{periodRow}</Rows> : null}
        {/*
          div.access-card `4952:6262` belongs to the access-changed / restricted-session frame
          (`4952:6229`, figma-spec-v2.md section 8), so it is drawn for the outcome that means the
          content is no longer permitted. It is NOT the home of `answer.restrictions`, which
          contract section 5 states as free text on an ordinary answered turn.
        */}
        {turn.outcome === 'permission_restricted' ? (
          <OrbitAccessCard body={answer.restrictions} />
        ) : null}
      </Prose>
    );
  }

  return (
    <OrbitAnalysisCard
      variant={variant}
      testId="orbit-answer"
      label={(() => {
        if (outcomeCopy !== null) {
          return t(outcomeCopy.labelKey);
        }
        // No frame draws a head on the bars answer; only the donut keeps its tag.
        return variant === 'donut' ? analysisLabel.rest : undefined;
      })()}
      labelTestId={outcomeCopy !== null ? 'orbit-answer-outcome' : 'orbit-answer-insight'}
      labelIcon={
        outcomeCopy !== null ? (
          <outcomeCopy.Icon aria-hidden="true" />
        ) : variant === 'donut' && analysisLabel.Icon !== undefined ? (
          <OrbitGlyphIcon Icon={analysisLabel.Icon} />
        ) : null
      }
      negative={outcomeCopy !== null && outcomeCopy.tone === 'negative'}
      text={answer.answer}
      textTestId="orbit-answer-text"
    >
      {/*
        Processing -> Success / Failure -> View in Reports (FR section 4.13, Desktop-13 and
        Desktop-14). `OrbitReportStatus` owns the whole journey, including the poll.
      */}
      {turn.outcome === 'report_queued' ? (
        <OrbitReportStatus reportDownloadId={turn.report_download_id} />
      ) : null}
      {turn.outcome === 'answered' ? (
        <Rows>
          <DetailRow id="scope" label={t(`${TOrbit}.answer.scope`)} value={answer.scope} />
          {periodRow}
          {chart !== undefined && chart !== null ? (
            <ChartSlot data-testid="orbit-answer-chart">{chart}</ChartSlot>
          ) : null}
          {answer.evidence.length > 0 ? (
            <Row data-testid="orbit-answer-explanation">
              <OrbitSectionLabel as="dt">{t(`${TOrbit}.answer.explanation`)}</OrbitSectionLabel>
              <RowContent>
                <OrbitChipRow>
                  {answer.evidence.map(metricId => (
                    <OrbitChip
                      key={metricId}
                      title={metricId}
                      data-testid={`orbit-answer-evidence-${metricId}`}
                    >
                      {evidenceName(metricId)}
                    </OrbitChip>
                  ))}
                </OrbitChipRow>
              </RowContent>
            </Row>
          ) : null}
        </Rows>
      ) : null}
      {turn.outcome === 'answered' && confidence !== null ? (
        <OrbitAnswerFooter confidence={confidence} turn={turn} />
      ) : null}
      {/* plan.md:55 orders the answer `... explanation -> confidence -> data status ->
          restrictions -> next step`, so these two follow the confidence pill rather than closing the
          detail list above it. */}
      {turn.outcome === 'answered' && (answer.data_quality !== '' || answer.restrictions !== '') ? (
        <Rows>
          <DetailRow
            id="data-status"
            label={t(`${TOrbit}.answer.dataStatus`)}
            value={answer.data_quality}
          />
          <DetailRow
            id="restrictions"
            label={t(`${TOrbit}.answer.restrictions`)}
            value={answer.restrictions}
          />
        </Rows>
      ) : null}
      {turn.outcome === 'answered' && answer.next_step !== '' ? (
        <>
          <SuggestedLabel>{t(`${TOrbit}.answer.suggested`)}</SuggestedLabel>
          {/*
            A real button, not the static row it shipped as (user, 2026-09-14: "Suggested Actions is
            look bit off, is it button or ?"). The frame answers that question itself: `4952:8123`
            has a HOVER variant `4952:4968` (figma-spec-v3.md §4c) that moves the stroke to #268AFF
            and the ink to #F5F7FB, and only an interactive row is drawn hovered. So it submits its
            own text the way a home-state starter does.
          */}
          <OrbitActionRow
            label={answer.next_step}
            testId="orbit-answer-next-step"
            disabled={isAsking}
            onSelect={() => ask(answer.next_step)}
          />
        </>
      ) : null}
    </OrbitAnalysisCard>
  );
};

export default OrbitAnswer;
